/**
 * The mobile layer's single control panel.
 *
 * EVERY mobile behaviour in this app is reachable from this file. Nothing under
 * src/mobile/ runs unless `ENABLED` is true, and each screen and feature has its
 * own flag beneath it. That is the contract: to switch mobile off — wholly or in
 * part — you edit this file and nothing else. No desktop file needs reading, and
 * none needs changing.
 *
 * House style is src/modules/patient/sections/index.js: a module-scoped object,
 * default-exported, with the reasoning kept next to the value rather than in a
 * commit message.
 */

import { CC_V2_ADD, CC_V2_EDIT } from "modules/patient/sections/chiefComplaints/events";
import { DX_V2_ADD, DX_V2_STATUS } from "modules/patient/sections/diagnosis/events";
import { MED_V2_ADD, MED_V2_STATUS } from "modules/patient/sections/medicines/events";
import { ALG_V2_ADD, ALG_V2_EDIT } from "modules/patient/sections/allergies/events";
import { FAM_V2_ADD, FAM_V2_EDIT } from "modules/patient/sections/familyHistory/events";
import { LI_V2_EDIT, LI_V2_DIGITISE } from "modules/patient/sections/labInvestigations/events";

/**
 * Width-only, at 767px — deliberately NOT `(pointer: coarse)`.
 *
 * A narrow desktop window therefore gets the mobile layout, which is how this
 * is tested: no device emulation needed, just drag the window. The cost is that
 * someone who happens to run a 700px-wide browser sees mobile; that is the
 * accepted trade, because user-agent sniffing fails on exactly the devices it
 * is meant to catch.
 *
 * Kept in sync by hand with $zym-bp in styles/mobile.scss — Sass cannot read a
 * JS constant, and a build step to share one number is not worth it for a value
 * that changes approximately never. Change one, change the other.
 */
export const BREAKPOINT_PX = 767;

/** The class useIsMobile stamps on <html>. Every mobile CSS rule is under it. */
export const ROOT_CLASS = "zy-mobile";

/**
 * Stamped beside ROOT_CLASS on iOS (WebKit) alone, behind `iosDateFields` —
 * for the few rules that must reach an iPhone and nothing else.
 */
export const IOS_CLASS = "zy-ios";

const MOBILE = {
  /**
   * The master switch. False here and the app is exactly what it was before
   * this directory existed: no root class, so no mobile CSS matches, and every
   * `isMobile` read is false, so every screen renders its desktop branch.
   * This is the acceptance test for "mobile code stays separated".
   */
  ENABLED: true,

  /* ---------- per-screen ---------- */

  /** /navigo-module/patient/:appointmentId */
  patientScreen: true,
  /** /navigo-module/prescription/:appointmentId */
  prescriptionScreen: true,

  /**
   * /navigo-module/appointment/:appointmentId — the bar only.
   *
   * This screen's body is already a stack of label/value cards that reads at
   * 360px, so it is left alone; what it lacked was a way back and a way to log
   * out. Off, it keeps the desktop breadcrumb bar.
   */
  appointmentDetails: true,
  /**
   * /navigo-module. Off, this falls back to the desktop table plus the earlier
   * @768 card block in appointments.scss — which still works, and is why that
   * block is kept rather than deleted.
   */
  appointmentsList: true,

  /* ---------- per-feature ---------- */

  /**
   * Create Rx: a pencil on every Chief complaints / Diagnosis / Medicine row,
   * opening that record's own update sheet on this screen.
   *
   * Desktop has no equivalent and gains none — the pencil renders only where
   * the host passes `onEdit`, and only the mobile branch does. Off, the rows
   * are what they were: a checkbox and a label.
   *
   * Lab investigations is not wired: the section was off limits when this was
   * built, and its readings were later made read-only on mobile by decision
   * (mobileViewWorking.md, Step 31).
   */
  rxRowEdit: true,

  /**
   * Create Rx's row 2 — the read-only date + status strip under the title.
   *
   * OFF. It restates the appointment this screen is already prescribing for,
   * and it cannot be acted on here: the status control was deliberately dropped
   * from it (changing status mid-build re-seeds the picker and undoes every box
   * ticked by hand — see MobilePrescriptionScreen), so the row was two facts
   * and no action, taking 52px from the card stack on every scroll.
   *
   * On restores the strip AND the 52px the pinned rows below it reserve — the
   * two are chained through `--zym-row2-h`, so neither moves without the other.
   */
  rxAppointmentStrip: false,

  /**
   * The patient screen's overflow (⋮) menu.
   *
   * OFF. Its only item is Log out, and this screen is always arrived at from
   * the appointments list, whose avatar offers the same thing beside the
   * account it belongs to — so the ⋮ was a second way to do one thing, on the
   * screen where the top bar is most crowded (back, a wrapping patient name,
   * the edit pencil and the address book already share that row).
   *
   * APPOINTMENT DETAILS KEEPS ITS OWN. That route wears the same MobileTopBar
   * but has no avatar anywhere on it, so turning the menu off there would leave
   * no way to log out of the screen you are on — which is the reason the bar
   * was given a menu in the first place. The flag is read by the patient screen
   * alone; `menu` is a per-usage prop.
   */
  patientOverflowMenu: false,

  /**
   * Lab investigations as cards (MobileLabList) instead of the desktop table.
   *
   * The one section drawn by its own render rather than by CSS over the desktop
   * rows: the card's "Ref 60–100 bpm" line is a value the table never renders.
   * Off, the tab shows the desktop table again.
   */
  labCards: true,

  /**
   * Adding a lab investigation opens "Digitise report" — the modal the desktop
   * card's "+ Add investigation" opens — rather than the single-reading form
   * (decision, 2026-09-17). ONE flag for both places a phone adds one: the
   * patient Lab tab's pinned button, and the Lab investigations card's "+ Add"
   * on Create Rx, where DigitiseLauncher hosts the modal because no lab list is
   * mounted there.
   *
   * Off, both fall back to ADD_EVENT_FOR_TAB, which opens that form in a sheet
   * as they did before.
   */
  labAddOpensDigitise: true,

  /**
   * Our own face over every native date field — "Select date" or
   * "17 Sep 2026" (DD MMM YYYY) beside a calendar icon — in place of the platform's empty
   * box and dropdown chevron (dateLayer.js). The native input stays underneath
   * as the control, so picking, limits and saving are unchanged.
   *
   * Off, date fields show whatever the platform draws, as before.
   */
  dateLayer: true,

  /**
   * On an iPhone, a date field is as wide as its form makes it.
   *
   * WebKit on iOS draws `<input type="date">` with its native appearance, and
   * while it does it forces content-box sizing and sizes the field from its own
   * text, ignoring `width: 100%`. The filter sheet's From/To ran past the sheet;
   * Add medicine's Start date and Patient details' Date of birth sat as a stub
   * at the left of their row. The date face follows the input, so it did too.
   * On, useIsMobile stamps IOS_CLASS and mobile.scss takes the appearance off —
   * on iOS only, because on Chrome Android that blanks the date text (Step 45).
   *
   * Off, iOS draws its date fields at its own width again.
   */
  iosDateFields: true,

  /**
   * On an iPhone, tapping a field does not zoom the page.
   *
   * WebKit on iOS zooms in on any field whose text is under 16px when it
   * takes focus, and stays zoomed after it loses it. Ours are 13–14px, to the
   * board; login's number field is 14px at phone width. iosInputZoom.js adds
   * `maximum-scale=1` to the viewport tag — iOS reads that as "no zoom on
   * focus" and, since iOS 10, ignores it for pinch, so pinch-zoom stays. iOS
   * only, because Android Chrome would take pinch-zoom away; mobile width
   * only, so desktop and iPads keep their own. Independent of iosDateFields.
   *
   * Off, iOS zooms into small fields again. No effect off iOS either way.
   */
  iosNoInputZoom: true,

  /**
   * On an iPhone, a drag over an open sheet (or anything above it) does not
   * move the page behind it.
   *
   * The sheets' scroll lock is `overflow: hidden` on <html>, and with the
   * keyboard up iOS pans the visible area over the page regardless — a drag
   * on the filter sheet's Account code popover scrolled the appointment list
   * behind it. On, while the lock is held, scrollLock.js cancels a one-finger
   * drag unless something under the finger can still scroll that way (the
   * popover's list, a sheet's body). Pinch-zoom is untouched.
   *
   * Off, the overflow lock alone, as before. No effect off iOS either way.
   */
  iosTouchScrollLock: true,

  /**
   * Create Rx's Advised investigations search opens its results in a bottom
   * sheet instead of the anchored popover (LabTestSearch).
   *
   * The popover closed on an outside tap, a scroll or a resize — exactly how a
   * phone's keyboard is put away — and kept "Add selected" behind the keyboard.
   * The sheet closes only on ✕ or its backdrop. Off, the popover returns.
   */
  rxLabSearchSheet: true,

  /**
   * Create Rx: the Patient details card's Weight is a button that opens
   * Anthropometric data's own Weight sheet on this screen. Save is the
   * section's own POST (with its version check); the card then re-reads the
   * latest weigh-in. Off: Weight is the read-only field desktop shows.
   */
  rxWeightSheet: true,

  /**
   * The patient screen's row 2 carries the appointment's REASON and its
   * APPOINTMENT DOCUMENTS button under the date/status line (board 5330:2).
   * Both stay in the detail sheet either way. Off: row 2 is the single line of
   * date, status and chevron, and the rows below it move up with it.
   */
  apptStripDetail: true,

  /**
   * The patient screen's own Documents tab: the appointment sheet's "All
   * documents" — the folder grid, a folder's files, and editing a document's
   * details — as a panel, with "+ Upload" in the bar at the foot of the
   * screen. `DocumentsMenu variant="inline"`, the same component and the same
   * fetch. Off: the tab is gone and documents stay in the sheet's popover
   * only. Desktop never had this tab.
   */
  documentsTab: true,

  /**
   * Upload document's "Browse files" (and its Retry) opens an "Upload from"
   * sheet first: Camera, Photos, Files. Each row is its own hidden file input,
   * so a phone goes straight to the camera (`capture`) or the gallery (an
   * image-only `accept`) instead of the one generic picker — on Android 14/15
   * Chrome's picker offers no camera at all. The picked file goes into the
   * modal's own `takeFile`, the path its own input uses. Off: both buttons open
   * the platform picker directly, as on desktop.
   */
  uploadSourceSheet: true,

  /**
   * Long text on the record's cards stops after READ_MORE_LINES lines, with
   * "Read more" / "Show less" under it — descriptions run to 250 words, and
   * one card could fill several screens. Covers every tab that shows long
   * text, through the three components that draw it: MetaTooltip (Chief
   * complaints, Allergies, Family history, Medicines), BulletListCell
   * (Diagnosis) and NoteCard (Progress notes). Also the appointment sheet's
   * Reason, whose free text can run to half a page (Step 85). The button
   * appears only when the text really is cut. Off: the full text runs on, as
   * before.
   */
  readMore: true,
  /** Where `readMore` cuts the text (user, 2026-10-05). */
  READ_MORE_LINES: 5,

  /**
   * "Update booking for" in the appointment-details sheet — the header's own
   * control, moved into the sheet rather than rebuilt.
   *
   * Desktop puts it in the patient header's field strip, which mobile does not
   * draw; the sheet is where this screen keeps everything else about the
   * appointment. Off, the sheet has Reschedule and Call note as before and the
   * switch is simply not offered on a phone.
   */
  updateBookingFor: true,

  /**
   * The page behind the Patient details dialog stays put while it is open — on
   * the patient record (the name's pencil) and on Create Rx ("Update").
   *
   * The dialog is desktop's, and takes no scroll lock of its own. On a phone
   * <html> is what scrolls, so a drag on the dialog or its dimmed backdrop moved
   * the record behind it instead, and a dialog that fits the screen had nothing
   * of its own to scroll at all. The screen that opens it now holds the layer's
   * shared lock (scrollLock.js) while it is up. Off, the page scrolls behind the
   * dialog again; the dialog's mobile CSS (its height and scroll containment)
   * follows ENABLED alone.
   */
  patientDetailsScrollLock: true,

  /**
   * TOUCH gestures are all the platform's own, and that is a decision rather
   * than an omission.
   *
   * Horizontal swiping belongs to the tab rail alone, and the rail gets it from
   * `overflow-x: auto` — momentum scrolling no hand-rolled touch handler
   * matches. Swiping the CONTENT area does not change tab: a section table that
   * is itself scrolled sideways would be fighting the page for the same
   * gesture, and the table has the better claim.
   *
   * Nor is there swipe-to-dismiss on the sheet. Sheet content is long and
   * scrolls, so a downward drag has to mean "scroll" — a sheet that could also
   * be thrown away by dragging down would eat the first scroll of every form.
   * Dismissal is the always-visible X and the backdrop.
   */

  /**
   * Drag with a MOUSE to pan, as a finger pans on a phone.
   *
   * The breakpoint is width-only, so this layout is what you get by making a
   * desktop window narrow — which is how it is built and reviewed. A narrow
   * window has no finger in it: the wheel scrolls, the scrollbar scrolls, and
   * dragging does nothing. This closes that gap.
   *
   * It cannot affect a real phone: the handler ignores every pointer that is
   * not a mouse, and a finger never produces one. See useDragScroll for why
   * that guard is load-bearing rather than tidy.
   */
  dragScroll: true,

  /**
   * Scrollbars are hidden throughout. A phone has none, so a narrow desktop
   * window showing them is showing something the design never has — and the
   * tab rail and status strip already hide theirs for that reason.
   *
   * NOT READ BY ANY CODE YET: the rules follow ENABLED alone, so setting this
   * false changes nothing (mobileViewWorking.md, section 4).
   */
  hideScrollbars: true,

  sheet: {
    /** Tapping the dimmed area closes the sheet. The X always does too. */
    dismissOnBackdrop: true,
  },

  /**
   * "View trend" opens a wide chart modal that cannot be read at 360px, and
   * every section has one. Hidden across the board rather than per section.
   *
   * NOT READ BY ANY CODE YET: the rules follow ENABLED alone, so setting this
   * false changes nothing (mobileViewWorking.md, section 4).
   */
  hideViewTrend: true,

  /**
   * Section tables re-flow into stacked cards. Applied per section as we go.
   *
   * NOT READ BY ANY CODE YET: the card CSS follows ENABLED alone, so setting
   * this false changes nothing. (Lab investigations' cards have a real switch,
   * `labCards`.)
   */
  tablesAsCards: true,

  /**
   * The preferred-languages popover in row 3 of the patient record.
   *
   * Off: the board (5330:2) has no line for it, and the control is a
   * desktop-width popover. Desktop keeps it either way — this only decides
   * whether mobile draws it, and the row makes space for it when true.
   */
  showPreferredLanguages: false,

  /**
   * Login: the mobile number and both OTP fields ask a phone for its NUMBER
   * pad (`inputMode="numeric"`, plus `pattern="[0-9]*"` for older iOS Safari)
   * instead of the full keyboard. Every one of them only ever takes digits —
   * their onChange strips anything else. Off: the fields are exactly desktop's
   * (the number stays `type="tel"`, the OTPs `type="text"`).
   */
  loginNumericKeypad: true,

  /**
   * Login: the page's own toast ("Enter a valid OTP!") sits BELOW the app's
   * toast stack (ToastHost, "Something went wrong.") instead of on the same
   * 24px line. A failed verify raises both at once, and at phone width the
   * two boxes landed on top of each other. ToastHost publishes the space its
   * stack takes as --zym-toasts-h; the login toast's `top` adds it. Off: both
   * sit at 24px again, as on desktop.
   */
  loginToastStack: true,

  /**
   * An ongoing SparkTG call (ZY-10312) shows as a green row pinned ABOVE row 1
   * instead of the desktop pill. The pill is fixed at the bottom centre, which
   * on a phone is exactly where the pinned Create Rx / Call patient bar sits,
   * so it covered both buttons. The row carries the same name and appointment
   * id and does the same thing on a tap — SparkTGCallFloater decides all of it,
   * and the row only draws it (MobileCallBar).
   *
   * Follows the mobile LAYER, not the width: Health plan and Book appointment
   * render their desktop tree at phone width and keep the pill with it.
   *
   * Off: the pill at phone width again, over the bar. Every pinned row's offset
   * adds --zym-call-h, which is 0 whenever no row is mounted, so nothing else
   * moves.
   */
  callBar: true,

  /**
   * Which tabs the horizontal rail offers, in order.
   *
   * "overview" is not a section — it is the two overview cards that sit above
   * the shell on desktop. Every other key MUST exist in
   * src/modules/patient/sections/index.js, because the rail mounts
   * SECTIONS[key].Content directly.
   *
   * The desktop sidebar's other nine rows (medical history, hospitalisation,
   * drug schedule, the whole Lifestyle group, ECHO - AI transcript) are absent
   * on purpose: they do not feed Create Rx, which is what this screen is for on
   * a phone. They are hidden, never removed — desktop still has all of them,
   * and adding a key back here is the only change needed to restore one.
   */
  /* THE ORDER IS THE PRODUCT'S, not the desktop rail's (user, 2026-09-23):
     what a consultation reaches for first comes first, and the reference
     sections trail. Desktop's sidebar keeps its own order — this list is read
     only by the mobile rail. */
  TABS: [
    { key: "overview", label: "Overview" },
    /* NOT a patient SECTION — there is no desktop tab for it. It is the
       appointment sheet's "All documents" as a panel of its own, which is why
       `documentsTab` can take it away without touching the rest. */
    { key: "documents", label: "Documents" },
    { key: "diagnosis", label: "Diagnosis" },
    { key: "chief-complaints", label: "Chief complaints" },
    { key: "medicines", label: "Medicines" },
    { key: "allergies", label: "Allergies" },
    /* "Anthropometric", not "Anthropometric data" — the rail is tight and the
     section's own heading says "data" one line below it. */
    { key: "anthropometric", label: "Anthropometric" },
    { key: "lab-investigations", label: "Lab investigations" },
    { key: "progress-notes", label: "Progress notes" },
    { key: "family-history", label: "Family history" },
  ],

  /**
   * What the fixed bottom bar's add button emits, per tab.
   *
   * These are the section's OWN existing add events — the identical constants
   * the in-card "+ Add" button emits (e.g. ChiefComplaints.js:157 emits
   * CC_V2_ADD and does nothing else). The bar is therefore a second trigger for
   * a path that already exists, not a new one.
   *
   * null means no bar button:
   *   overview        — gets Call patient / Create Rx instead
   *   progress-notes  — contentOnly, there is no editor to open
   *   anthropometric  — opens per PARAMETER (ANTH_V2_SELECT_PARAM carries a
   *                     key), so there is no single "add" to fire; the rows
   *                     themselves stay the way in
   *
   * lab-investigations rides LI_V2_EDIT with no payload, which useSheetStack
   * reads as "open a blank add" (useSheetStack.js:216-220) — that section has
   * no separate ADD event.
   */
  ADD_EVENT_FOR_TAB: {
    overview: null,
    "progress-notes": null,
    anthropometric: null,
    "chief-complaints": CC_V2_ADD,
    diagnosis: DX_V2_ADD,
    medicines: MED_V2_ADD,
    "lab-investigations": LI_V2_EDIT,
    allergies: ALG_V2_ADD,
    "family-history": FAM_V2_ADD,
  },

  /**
   * Where a tab's ADD should open something other than its add event above.
   * Read before ADD_EVENT_FOR_TAB, behind `labAddOpensDigitise`, by both the
   * patient screen's pinned button and Create Rx's goAddIn — today only Lab,
   * which opens Digitise report.
   *
   * A SECOND MAP rather than an edit to the entry above, so that one flag can
   * put both screens back on LI_V2_EDIT and the single-reading form. The two
   * screens reach the modal differently: the patient screen's LabInvestigations
   * owns one; Create Rx has no lab list, so its goAddIn sends the patient and
   * appointment on the event for DigitiseLauncher to open one.
   */
  BAR_EVENT_FOR_TAB: {
    "lab-investigations": LI_V2_DIGITISE,
  },

  /**
   * What the sheet is called once it is open, per tab.
   *
   * The tab's own name ("Chief complaints") is what a sheet says when nothing
   * better is known, but the board titles these by the ACTION — "Add complaint"
   * when the add event opened it, "Update complaint" when the edit event did.
   * The screen listens to both and takes the wording from whichever fired.
   *
   * Only sections with a distinct edit channel need an entry; the rest fall
   * back to the tab name. Anthropometric is absent on purpose: it opens per
   * parameter, so its title is the parameter's own name.
   */
  SHEET_TITLES: {
    "chief-complaints": {
      addEvent: CC_V2_ADD,
      editEvent: CC_V2_EDIT,
      add: "Add complaint",
      edit: "Update complaint",
    },
    /* Diagnosis has no DX_V2_EDIT — its rail's edit channel is DX_V2_STATUS,
       which the treatment chip fires (DiagnosisEditor.js:28-29). Wording
       matches the rail's own describe(): "Add diagnosis" / "Update …". */
    diagnosis: {
      addEvent: DX_V2_ADD,
      editEvent: DX_V2_STATUS,
      add: "Add diagnosis",
      edit: "Update diagnosis",
    },
    /* Medicines has a third channel, MED_V2_ADHERENCE, which is not add or
       edit — it opens the adherence form. It keeps the tab's own name, which
       is what the fall-through gives it. */
    medicines: {
      addEvent: MED_V2_ADD,
      editEvent: MED_V2_STATUS,
      add: "Add medicine",
      edit: "Update medicine",
    },
    allergies: {
      addEvent: ALG_V2_ADD,
      editEvent: ALG_V2_EDIT,
      add: "Add allergy",
      edit: "Update allergy",
    },
    /* "Add relative" / "Edit relative" — the form's OWN headings
       (RelativeForm.js:492), which is what the sheet sits directly above. The
       board's button says "Add family member" and that wording was used here
       first; with the form mounted inside the sheet it read as two different
       names for one thing, so the form wins (product, 2026-09-16). */
    "family-history": {
      addEvent: FAM_V2_ADD,
      editEvent: FAM_V2_EDIT,
      add: "Add relative",
      edit: "Edit relative",
    },
  },

  /**
   * Create Rx: which update sheet a picker row's pencil opens, and the payload
   * that section's rail expects.
   *
   * KEYED BY PICKER KEY ("complaints"), not by section key ("chief-complaints")
   * — the Rx card knows its picker, and PATIENT_SECTION_FOR_PICKER already owns
   * the translation for the add path. Going through it here would buy nothing
   * and add a second hop to read.
   *
   * These are the sections' OWN edit channels — the same ones their status
   * chips fire on the patient record, already listed in SHEET_TITLES above.
   * Nothing new is broadcast and no form is re-implemented: MobileRxAddSheet
   * keeps every rail mounted, so they answer here exactly as they do there.
   *
   * THE PAYLOAD SHAPE DIFFERS PER SECTION because each rail unwraps its own —
   * ChiefComplaintEditor reads `payload.complaint` (:22), DiagnosisEditor
   * `payload.diagnosis` (:31), MedicinesEditor `payload.medicine` plus a `mode`
   * marker (:29-38) that tells an update sheet from an adherence review. Get
   * the marker wrong and the doctor gets a blank adherence form instead of the
   * medicine's regimen, which is why it is stated here rather than assumed.
   *
   * Lab investigations is absent on purpose — see `rxRowEdit`.
   */
  EDIT_FOR_PICKER: {
    complaints: { event: CC_V2_EDIT, payload: (record) => ({ complaint: record }) },
    diagnosis: { event: DX_V2_STATUS, payload: (record) => ({ diagnosis: record }) },
    medicines: {
      event: MED_V2_STATUS,
      payload: (record) => ({ medicine: record, mode: "status" }),
    },
  },

  /** The bar button's wording, per tab. Matches each section's own add button. */
  ADD_LABEL_FOR_TAB: {
    "chief-complaints": "Add complaint",
    diagnosis: "Add diagnosis",
    medicines: "Add medicine",
    "lab-investigations": "Add lab investigation",
    allergies: "Add allergy",
    "family-history": "Add family history",
  },
};

export default MOBILE;
